import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { t as tRaw } from '@/locales/strings';

// Minimal offline indicator. Listens for window online/offline events
// and slides a small banner down from the top of the viewport while
// the user is offline. Does not block interaction — writes still queue
// to localStorage via safeWrite, and Supabase upserts will retry once
// the browser regains connectivity on the next user action. The
// banner only exists to set expectations; we are not building a full
// sync queue here.

export default function OfflineBanner() {
  const { lang } = useApp();
  const [offline, setOffline] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return navigator.onLine === false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 40000,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: '#5A3E2B',
        color: '#FAF6F1',
        textAlign: 'center',
        fontFamily: "var(--font-ui, 'DM Sans', 'Lato', system-ui, sans-serif)",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.02em',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <div style={{ padding: '8px 16px' }}>
        {tRaw('offline_banner', lang)}
      </div>
    </div>
  );
}
